import { useMemo } from 'react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { availableOutputFormats } from './imageOutputFormats';
import { outputFormatIcon } from './outputFormatIcons';

function FormatOption({ id, label }) {
    const Icon = outputFormatIcon(id);
    return (
        <span className="flex items-center gap-2">
            <Icon size={16} stroke={1.8} className="shrink-0 text-[#A8A099] dark:text-zinc-500" />
            {label}
        </span>
    );
}

/**
 * What the finished file comes out as.
 *
 * It lives in the action bar rather than in the settings column because it is
 * about the button beside it: the run button renames itself to whatever is picked
 * here, so the pair reads as one sentence ("JPG · Download JPG"). Built on the
 * app's own Select rather than a bare `<select>`, whose native popup ignores the
 * theme and looked like a browser menu dropped on the page.
 *
 * `formats` is the tool's own list, in the order it wants them offered: an A4
 * sheet is a PDF or a picture of one, a QR code is a picture or a vector. Pass a
 * constant, not a fresh array — the list is only filtered when it changes.
 * Whatever this browser cannot write is dropped, except AVIF, which has a
 * WebAssembly encoder behind it.
 */
export default function OutputFormatSelect({ value, onChange, disabled = false, formats }) {
    const { t } = useTranslation();
    const options = useMemo(() => availableOutputFormats(formats), [formats]);

    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger
                aria-label={t('toolPage.outputFormat.title')}
                className="h-[46px] w-auto shrink-0 justify-start gap-2 whitespace-nowrap rounded-xl border-[#ECE8E4] bg-white px-3.5 text-[14px] font-semibold text-[#3D3833] shadow-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
            >
                <span className="text-[12px] font-medium text-[#A8A099] dark:text-zinc-500">
                    {t('toolPage.outputFormat.title')}
                </span>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {options.map((id) => (
                    <SelectItem key={id} value={id}>
                        <FormatOption id={id} label={t(`toolPage.outputFormat.formats.${id}`)} />
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
